import { z } from 'zod';

import { CursorPaginationMetaSchema, PaginationSchema } from '../../pagination.js';

import { ConcreteCardAcquirerSchema, ConcreteCardBrandSchema } from './card-fee-rates.schemas.js';

const CentsSchema = z.string().regex(/^\d+$/, 'must be a non-negative integer string (cents)');

export const PaymentMethodSchema = z.enum([
  'CASH',
  'PIX',
  'CREDIT_CARD',
  'DEBIT_CARD',
  'BOLETO',
  'BANK_TRANSFER',
  'PAYMENT_LINK',
  'CHEQUE',
  'VOUCHER',
  'STORE_INSTALLMENT',
]);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const PaymentSourceTypeSchema = z.enum(['SERVICE_ORDER']);
export type PaymentSourceType = z.infer<typeof PaymentSourceTypeSchema>;

// GET /finance/comandas
export const ListPayableComandasQuerySchema = PaginationSchema;
export type ListPayableComandasQuery = z.infer<typeof ListPayableComandasQuerySchema>;

export const PayableComandaListItemSchema = z.object({
  serviceOrderId: z.uuid(),
  placeId: z.uuid(),
  status: z.string(),
  partyDisplayName: z.string(),
  vehicleDisplayName: z.string().nullable(),
  openedAt: z.iso.datetime(),
  totalDueCents: CentsSchema,
  displayNumber: z.string(),
});
export type PayableComandaListItem = z.infer<typeof PayableComandaListItemSchema>;

export const ListPayableComandasResponseSchema = z.object({
  items: z.array(PayableComandaListItemSchema),
  pagination: CursorPaginationMetaSchema,
  openCount: z.number().int().nonnegative(),
  openTotalCents: CentsSchema,
  paidTodayCount: z.number().int().nonnegative(),
  paidTodayTotalCents: CentsSchema,
  paidTodayOsCount: z.number().int().nonnegative(),
  paidTodayOsTotalCents: CentsSchema,
});
export type ListPayableComandasResponse = z.infer<typeof ListPayableComandasResponseSchema>;

function isSundayIsoDate(value: string): boolean {
  const parts = value.split('-');
  if (parts.length !== 3) {
    return false;
  }
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return false;
  }
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay() === 0;
}

// GET /finance/cashier-weekly
export const GetCashierWeeklyQuerySchema = z
  .object({
    placeId: z.uuid(),
    weekStart: z.iso.date().optional(),
  })
  .superRefine((query, ctx) => {
    if (query.weekStart !== undefined && !isSundayIsoDate(query.weekStart)) {
      ctx.addIssue({
        code: 'custom',
        message: 'weekStart must be a Sunday (ISO date)',
        path: ['weekStart'],
      });
    }
  });
export type GetCashierWeeklyQuery = z.infer<typeof GetCashierWeeklyQuerySchema>;

export const CashierWeeklyDaySchema = z.object({
  date: z.iso.date(),
  openCents: CentsSchema,
  receivedOsCents: CentsSchema,
  pdvCents: CentsSchema,
});
export type CashierWeeklyDay = z.infer<typeof CashierWeeklyDaySchema>;

export const GetCashierWeeklyResponseSchema = z.object({
  weekStart: z.iso.date(),
  days: z.array(CashierWeeklyDaySchema).length(7),
});
export type GetCashierWeeklyResponse = z.infer<typeof GetCashierWeeklyResponseSchema>;

// GET /finance/comandas/:serviceOrderId
export const ComandaIdParamsSchema = z.object({
  serviceOrderId: z.uuid(),
});

export const ComandaItemSchema = z.object({
  id: z.uuid(),
  itemType: z.string(),
  description: z.string(),
  quantity: z.string(),
  unitPriceCents: CentsSchema,
  totalCents: CentsSchema,
  currency: z.string(),
});
export type ComandaItem = z.infer<typeof ComandaItemSchema>;

export const ComandaDetailResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  placeId: z.uuid(),
  status: z.string(),
  partyDisplayName: z.string(),
  vehicleDisplayName: z.string().nullable(),
  items: z.array(ComandaItemSchema),
  subtotalCents: CentsSchema,
  displayNumber: z.string(),
});
export type ComandaDetailResponse = z.infer<typeof ComandaDetailResponseSchema>;

// POST /finance/payments
export const RegisterPaymentLineBodySchema = z
  .object({
    method: PaymentMethodSchema,
    amountCents: CentsSchema,
    receivedCents: CentsSchema.nullish(),
    cardBrand: ConcreteCardBrandSchema.optional(),
    acquirer: ConcreteCardAcquirerSchema.optional(),
    installmentCount: z.number().int().positive().optional(),
  })
  .superRefine((line, ctx) => {
    if (line.method !== 'CASH' && line.receivedCents !== undefined && line.receivedCents !== null) {
      ctx.addIssue({
        code: 'custom',
        message: 'received_cents is only allowed for CASH',
        path: ['receivedCents'],
      });
    }
    const isCard = line.method === 'CREDIT_CARD' || line.method === 'DEBIT_CARD';
    if (!isCard) {
      if (
        line.cardBrand !== undefined ||
        line.acquirer !== undefined ||
        line.installmentCount !== undefined
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'card context is only allowed for credit and debit',
          path: ['cardBrand'],
        });
      }
      return;
    }
    if (
      line.cardBrand === undefined ||
      line.acquirer === undefined ||
      line.installmentCount === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'card brand, acquirer and installment count are required',
        path: ['cardBrand'],
      });
      return;
    }
    if (line.method === 'DEBIT_CARD' && line.installmentCount !== 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'debit accepts a single installment',
        path: ['installmentCount'],
      });
    }
  });
export type RegisterPaymentLineBody = z.infer<typeof RegisterPaymentLineBodySchema>;

export const RegisterPaymentBodySchema = z.object({
  sourceType: PaymentSourceTypeSchema,
  sourceId: z.uuid(),
  discountCents: CentsSchema.optional().default('0'),
  lines: z.array(RegisterPaymentLineBodySchema).min(1),
});
export type RegisterPaymentBody = z.infer<typeof RegisterPaymentBodySchema>;

export const RegisterPaymentLineResponseSchema = z.object({
  paymentId: z.uuid(),
  method: PaymentMethodSchema,
  amountCents: CentsSchema,
  receivedCents: CentsSchema.nullable(),
  changeCents: CentsSchema.nullable(),
  cardBrand: ConcreteCardBrandSchema.nullable(),
  acquirer: ConcreteCardAcquirerSchema.nullable(),
  installmentCount: z.number().int().nullable(),
  cardFeePercentBps: z.number().int().nullable(),
  cardFeeCents: CentsSchema.nullable(),
});

export const RegisterPaymentResponseSchema = z.object({
  settlementGroupId: z.uuid(),
  sourceType: PaymentSourceTypeSchema,
  sourceId: z.uuid(),
  totalCents: CentsSchema,
  discountCents: CentsSchema,
  lines: z.array(RegisterPaymentLineResponseSchema),
});
export type RegisterPaymentResponse = z.infer<typeof RegisterPaymentResponseSchema>;

// --- Plano de contas (BR-FIN-08..10) ---

export const ChartOfAccountTypeSchema = z.enum(['SYNTHETIC', 'ANALYTIC']);
export type ChartOfAccountType = z.infer<typeof ChartOfAccountTypeSchema>;

export const ChartOfAccountNatureSchema = z.enum([
  'ASSET',
  'LIABILITY',
  'EQUITY',
  'REVENUE',
  'EXPENSE',
]);
export type ChartOfAccountNature = z.infer<typeof ChartOfAccountNatureSchema>;

export const DreSectionSchema = z.enum([
  'NONE',
  'GROSS_REVENUE',
  'REVENUE_DEDUCTION',
  'COGS',
  'DIRECT_LABOR',
  'OPERATING_EXPENSE',
  'PERSONNEL',
  'FINANCIAL_INCOME',
  'FINANCIAL_EXPENSE',
  'DEPRECIATION',
  'NON_OPERATING',
  'INCOME_TAX',
]);
export type DreSection = z.infer<typeof DreSectionSchema>;

export const CashFlowSectionSchema = z.enum(['NONE', 'OPERATING', 'INVESTING', 'FINANCING']);
export type CashFlowSection = z.infer<typeof CashFlowSectionSchema>;

export const TitleOperationKindSchema = z.enum([
  'OPERATING',
  'MERCHANDISE_PURCHASE',
  'LOAN_AMORTIZATION',
  'CUSTOMER_ADVANCE',
]);
export type TitleOperationKind = z.infer<typeof TitleOperationKindSchema>;

const ChartOfAccountSemanticFieldsSchema = {
  dreSection: DreSectionSchema,
  cashFlowSection: CashFlowSectionSchema,
  accountKey: z.string(),
  isSystem: z.boolean(),
  acceptsManualEntry: z.boolean(),
  isContra: z.boolean(),
  isElimination: z.boolean(),
  templateVersion: z.string().nullable(),
};

// POST /finance/chart-of-accounts
export const CreateChartOfAccountBodySchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(160),
  type: ChartOfAccountTypeSchema,
  parentId: z.uuid(),
});
export type CreateChartOfAccountBody = z.infer<typeof CreateChartOfAccountBodySchema>;

// PATCH /finance/chart-of-accounts/:id
export const UpdateChartOfAccountBodySchema = z.object({
  name: z.string().trim().min(1).max(160),
  type: ChartOfAccountTypeSchema,
});
export type UpdateChartOfAccountBody = z.infer<typeof UpdateChartOfAccountBodySchema>;

export const ChartOfAccountResponseSchema = z.object({
  id: z.uuid(),
  parentId: z.uuid().nullable(),
  code: z.string(),
  name: z.string(),
  type: ChartOfAccountTypeSchema,
  nature: ChartOfAccountNatureSchema,
  ...ChartOfAccountSemanticFieldsSchema,
  level: z.number().int().nonnegative(),
  archivedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime().optional(),
  updatedAt: z.iso.datetime().optional(),
});
export type ChartOfAccountResponse = z.infer<typeof ChartOfAccountResponseSchema>;

// GET /finance/chart-of-accounts
export const ChartOfAccountListItemSchema = z.object({
  id: z.uuid(),
  parentId: z.uuid().nullable(),
  code: z.string(),
  name: z.string(),
  type: ChartOfAccountTypeSchema,
  nature: ChartOfAccountNatureSchema,
  ...ChartOfAccountSemanticFieldsSchema,
  level: z.number().int().nonnegative(),
  archivedAt: z.iso.datetime().nullable(),
});
export type ChartOfAccountListItem = z.infer<typeof ChartOfAccountListItemSchema>;

export const ListChartOfAccountsResponseSchema = z.object({
  items: z.array(ChartOfAccountListItemSchema),
});
export type ListChartOfAccountsResponse = z.infer<typeof ListChartOfAccountsResponseSchema>;

// POST /finance/chart-of-accounts/:id/archive
export const ChartOfAccountIdParamsSchema = z.object({
  id: z.uuid(),
});

export const ArchiveChartOfAccountResponseSchema = z.object({
  id: z.uuid(),
  archivedAt: z.iso.datetime(),
});
export type ArchiveChartOfAccountResponse = z.infer<typeof ArchiveChartOfAccountResponseSchema>;

// GET /finance/chart-of-accounts/template-delta
export const ChartOfAccountsTemplateDeltaResponseSchema = z.object({
  tenantVersion: z.string().nullable(),
  latestVersion: z.string(),
  missingEntryKeys: z.array(z.string()),
});
export type ChartOfAccountsTemplateDeltaResponse = z.infer<
  typeof ChartOfAccountsTemplateDeltaResponseSchema
>;

// POST /finance/chart-of-accounts/template/adopt
export const AdoptChartOfAccountsTemplateBodySchema = z.object({});
export type AdoptChartOfAccountsTemplateBody = z.infer<
  typeof AdoptChartOfAccountsTemplateBodySchema
>;

export const AdoptChartOfAccountsTemplateResponseSchema = z.object({
  appliedVersion: z.string(),
  addedCount: z.number().int().nonnegative(),
  preservedRenameCount: z.number().int().nonnegative(),
  conflictCount: z.number().int().nonnegative(),
});
export type AdoptChartOfAccountsTemplateResponse = z.infer<
  typeof AdoptChartOfAccountsTemplateResponseSchema
>;

// --- Contas financeiras (BR-FIN-18) ---

export const FinancialAccountKindSchema = z.enum(['CHECKING', 'CASH']);
export type FinancialAccountKind = z.infer<typeof FinancialAccountKindSchema>;

/** Bank product type on CHECKING accounts (BR-FIN-40). */
export const BankAccountTypeSchema = z.enum(['CURRENT', 'SAVINGS', 'PAYMENT']);
export type BankAccountType = z.infer<typeof BankAccountTypeSchema>;

// POST /finance/financial-accounts
export const CreateFinancialAccountBodySchema = z.object({
  placeId: z.uuid(),
  name: z.string().trim().min(1).max(120),
  kind: FinancialAccountKindSchema,
  openingBalanceCents: CentsSchema.optional().default('0'),
});
export type CreateFinancialAccountBody = z.infer<typeof CreateFinancialAccountBodySchema>;

export const FinancialAccountResponseSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: FinancialAccountKindSchema,
  openingBalanceCents: CentsSchema,
  archivedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type FinancialAccountResponse = z.infer<typeof FinancialAccountResponseSchema>;

// PATCH /finance/financial-accounts/:id
export const UpdateFinancialAccountBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  openingBalanceCents: CentsSchema,
});
export type UpdateFinancialAccountBody = z.infer<typeof UpdateFinancialAccountBodySchema>;

export const UpdateFinancialAccountResponseSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: FinancialAccountKindSchema,
  openingBalanceCents: CentsSchema,
  archivedAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
});
export type UpdateFinancialAccountResponse = z.infer<typeof UpdateFinancialAccountResponseSchema>;

// POST /finance/financial-accounts/:id/archive
export const ArchiveFinancialAccountResponseSchema = z.object({
  id: z.uuid(),
  archivedAt: z.iso.datetime(),
});
export type ArchiveFinancialAccountResponse = z.infer<typeof ArchiveFinancialAccountResponseSchema>;

// GET /finance/financial-accounts?placeId=
export const ListFinancialAccountsQuerySchema = z.object({
  placeId: z.uuid().optional(),
});
export type ListFinancialAccountsQuery = z.infer<typeof ListFinancialAccountsQuerySchema>;

export const FinancialAccountListItemSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  name: z.string(),
  kind: FinancialAccountKindSchema,
  openingBalanceCents: CentsSchema,
  currentBalanceCents: CentsSchema,
  bankCode: z.string().nullable(),
  agency: z.string().nullable(),
  agencyDigit: z.string().nullable(),
  accountNumber: z.string().nullable(),
  accountDigit: z.string().nullable(),
  bankAccountType: BankAccountTypeSchema.nullable(),
  ispb: z.string().nullable(),
  holderName: z.string().nullable(),
  holderDocument: z.string().nullable(),
  archivedAt: z.iso.datetime().nullable(),
});
export type FinancialAccountListItem = z.infer<typeof FinancialAccountListItemSchema>;

export const ListFinancialAccountsResponseSchema = z.object({
  items: z.array(FinancialAccountListItemSchema),
});
export type ListFinancialAccountsResponse = z.infer<typeof ListFinancialAccountsResponseSchema>;

// --- Contas a pagar (BR-FIN-11,14..17,19,26) ---

export const PayableStatusSchema = z.enum(['OPEN', 'PAID', 'CANCELLED']);
export type PayableStatus = z.infer<typeof PayableStatusSchema>;

/** `OVERDUE` and `OPEN` (not overdue) both persist as `OPEN` — derived at read time (BR-FIN-15). */
export const PayableStatusFilterSchema = z.enum(['all', 'OPEN', 'OVERDUE', 'PAID', 'CANCELLED']);
export type PayableStatusFilter = z.infer<typeof PayableStatusFilterSchema>;

export const CreatePayableModeSchema = z.enum(['SINGLE', 'INSTALLMENTS']);
export type CreatePayableMode = z.infer<typeof CreatePayableModeSchema>;

// POST /finance/payables
export const CreatePayableBodySchema = z
  .object({
    placeId: z.uuid().nullish(),
    accountId: z.uuid(),
    // BR-FIN-26 — required only for MERCHANDISE_PURCHASE (enforced by the domain).
    payeePartyId: z.uuid().nullable().optional(),
    documentLabel: z.string().trim().min(1).max(80).nullish(),
    mode: CreatePayableModeSchema,
    totalAmountCents: CentsSchema,
    installmentCount: z.number().int().min(1).max(360).optional().default(1),
    issueDate: z.iso.date(),
    firstDueDate: z.iso.date(),
    operationKind: TitleOperationKindSchema.optional(),
    notes: z.string().trim().max(2000).nullish(),
  })
  .refine((body) => body.mode === 'SINGLE' || body.installmentCount >= 2, {
    message: 'installmentCount must be >= 2 when mode is INSTALLMENTS',
    path: ['installmentCount'],
  });
export type CreatePayableBody = z.infer<typeof CreatePayableBodySchema>;

export const CreatePayableItemResponseSchema = z.object({
  id: z.uuid(),
  installmentNumber: z.number().int().positive(),
  installmentCount: z.number().int().positive(),
  dueDate: z.iso.date(),
  amountCents: CentsSchema,
  status: PayableStatusSchema,
});

export const CreatePayableResponseSchema = z.object({
  documentGroupId: z.uuid(),
  items: z.array(CreatePayableItemResponseSchema),
});
export type CreatePayableResponse = z.infer<typeof CreatePayableResponseSchema>;

// PATCH /finance/payables/:id — manual OPEN titles only
export const UpdatePayableBodySchema = z.object({
  placeId: z.uuid().nullish(),
  accountId: z.uuid(),
  payeePartyId: z.uuid().nullable().optional(),
  documentLabel: z.string().trim().min(1).max(80).nullish(),
  amountCents: CentsSchema,
  issueDate: z.iso.date(),
  dueDate: z.iso.date(),
  operationKind: TitleOperationKindSchema.optional(),
  notes: z.string().trim().max(2000).nullish(),
});
export type UpdatePayableBody = z.infer<typeof UpdatePayableBodySchema>;

export const UpdatePayableResponseSchema = z.object({
  id: z.uuid(),
  documentLabel: z.string().nullable(),
  dueDate: z.iso.date(),
  amountCents: CentsSchema,
  status: PayableStatusSchema,
});
export type UpdatePayableResponse = z.infer<typeof UpdatePayableResponseSchema>;

// GET /finance/payables
export const ListPayablesQuerySchema = PaginationSchema.extend({
  status: PayableStatusFilterSchema.optional(),
  placeId: z.string().min(1).optional(),
  payeePartyId: z.uuid().optional(),
  q: z.string().trim().min(1).optional(),
});
export type ListPayablesQuery = z.infer<typeof ListPayablesQuerySchema>;

export const PayableOriginKindSchema = z.enum([
  'MANUAL',
  'INBOUND_NF',
  'PURCHASE_ORDER',
  'RECURRING',
]);
export type PayableOriginKind = z.infer<typeof PayableOriginKindSchema>;

export const PayableListItemSchema = z.object({
  id: z.uuid(),
  documentLabel: z.string().nullable(),
  payeeDisplayName: z.string(),
  placeId: z.uuid().nullable(),
  placeName: z.string().nullable(),
  accountId: z.uuid(),
  accountName: z.string(),
  issueDate: z.iso.date(),
  dueDate: z.iso.date(),
  amountCents: CentsSchema,
  status: PayableStatusSchema,
  isOverdue: z.boolean(),
  installmentNumber: z.number().int().positive(),
  installmentCount: z.number().int().positive(),
  originKind: PayableOriginKindSchema,
  recurringScheduleId: z.uuid().nullable(),
});
export type PayableListItem = z.infer<typeof PayableListItemSchema>;

export const ListPayablesResponseSchema = z.object({
  items: z.array(PayableListItemSchema),
  pagination: CursorPaginationMetaSchema,
  summary: z.object({
    totalPayableCents: CentsSchema,
    overdueCents: CentsSchema,
    openCents: CentsSchema,
    dueTodayCents: CentsSchema,
    paidThisMonthCents: CentsSchema,
  }),
});
export type ListPayablesResponse = z.infer<typeof ListPayablesResponseSchema>;

// POST /finance/payables/:id/settle
export const PayableIdParamsSchema = z.object({ id: z.uuid() });

export const PayableDetailResponseSchema = PayableListItemSchema.extend({
  payeePartyId: z.uuid().nullable(),
  notes: z.string().nullable(),
  operationKind: TitleOperationKindSchema,
  fiscalInboundDocumentId: z.uuid().nullable(),
  purchaseOrderId: z.uuid().nullable(),
});
export type PayableDetailResponse = z.infer<typeof PayableDetailResponseSchema>;

/** Conta fixa mensal a pagar (BR-FIN-65). */
export const RecurringPayableScheduleStatusSchema = z.enum(['ACTIVE', 'PAUSED', 'ENDED']);
export type RecurringPayableScheduleStatus = z.infer<typeof RecurringPayableScheduleStatusSchema>;

export const RecurringPayableScheduleIdParamsSchema = z.object({ id: z.uuid() });

export const CreateRecurringPayableScheduleBodySchema = z.object({
  placeId: z.uuid().nullable().optional(),
  accountId: z.uuid(),
  payeePartyId: z.uuid().nullable().optional(),
  expectedAmountCents: CentsSchema,
  dueDay: z.number().int().min(1).max(31),
  anchorDate: z.iso.date(),
  operationKind: TitleOperationKindSchema.optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export type CreateRecurringPayableScheduleBody = z.infer<
  typeof CreateRecurringPayableScheduleBodySchema
>;

export const RecurringPayableScheduleResponseSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid().nullable(),
  placeName: z.string().nullable().optional(),
  accountId: z.uuid(),
  accountName: z.string().optional(),
  payeePartyId: z.uuid().nullable(),
  payeeDisplayName: z.string().optional(),
  expectedAmountCents: CentsSchema,
  dueDay: z.number().int().min(1).max(31),
  anchorDate: z.iso.date(),
  operationKind: TitleOperationKindSchema,
  notes: z.string().nullable(),
  status: RecurringPayableScheduleStatusSchema,
  endedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type RecurringPayableScheduleResponse = z.infer<
  typeof RecurringPayableScheduleResponseSchema
>;

export const ListRecurringPayableSchedulesResponseSchema = z.object({
  items: z.array(RecurringPayableScheduleResponseSchema),
});
export type ListRecurringPayableSchedulesResponse = z.infer<
  typeof ListRecurringPayableSchedulesResponseSchema
>;

export const PauseRecurringPayableScheduleResponseSchema = z.object({
  id: z.uuid(),
  status: z.literal('PAUSED'),
});
export type PauseRecurringPayableScheduleResponse = z.infer<
  typeof PauseRecurringPayableScheduleResponseSchema
>;

export const ResumeRecurringPayableScheduleResponseSchema = z.object({
  id: z.uuid(),
  status: z.literal('ACTIVE'),
});
export type ResumeRecurringPayableScheduleResponse = z.infer<
  typeof ResumeRecurringPayableScheduleResponseSchema
>;

export const EndRecurringPayableScheduleResponseSchema = z.object({
  id: z.uuid(),
  status: z.literal('ENDED'),
  endedAt: z.iso.datetime(),
});
export type EndRecurringPayableScheduleResponse = z.infer<
  typeof EndRecurringPayableScheduleResponseSchema
>;

/** Free-text label (varchar(32), no closed set by design — e.g. "PIX", "Boleto", "Transferência"). */
const SettlementMethodSchema = z.string().trim().min(1).max(32);

export const SettlePayableBodySchema = z.object({
  financialAccountId: z.uuid(),
  settlementMethod: SettlementMethodSchema,
  settledAt: z.iso.date().optional(),
});
export type SettlePayableBody = z.infer<typeof SettlePayableBodySchema>;

export const SettlePayableItemResponseSchema = z.object({
  payableId: z.uuid(),
  status: z.literal('PAID'),
  settledAt: z.iso.datetime(),
});
export type SettlePayableItemResponse = z.infer<typeof SettlePayableItemResponseSchema>;

// POST /finance/payables/settle (batch — BR-FIN-16)
export const SettlePayablesBatchBodySchema = z.object({
  payableIds: z.array(z.uuid()).min(1).max(200),
  financialAccountId: z.uuid(),
  settlementMethod: SettlementMethodSchema,
  settledAt: z.iso.date().optional(),
});
export type SettlePayablesBatchBody = z.infer<typeof SettlePayablesBatchBodySchema>;

export const SettlePayablesBatchResponseSchema = z.object({
  items: z.array(SettlePayableItemResponseSchema),
});
export type SettlePayablesBatchResponse = z.infer<typeof SettlePayablesBatchResponseSchema>;

// POST /finance/payables/cancel (batch — BR-FIN-73)
export const CancelPayablesBatchBodySchema = z.object({
  payableIds: z.array(z.uuid()).min(1).max(200),
});
export type CancelPayablesBatchBody = z.infer<typeof CancelPayablesBatchBodySchema>;

export const CancelPayableItemResponseSchema = z.object({
  id: z.uuid(),
  status: z.literal('CANCELLED'),
});
export type CancelPayableItemResponse = z.infer<typeof CancelPayableItemResponseSchema>;

export const CancelPayablesBatchResponseSchema = z.object({
  items: z.array(CancelPayableItemResponseSchema),
});
export type CancelPayablesBatchResponse = z.infer<typeof CancelPayablesBatchResponseSchema>;

// --- Contas a receber (BR-FIN-11,13,14..17,19) ---

export const ReceivableStatusSchema = z.enum(['OPEN', 'PAID', 'CANCELLED']);
export type ReceivableStatus = z.infer<typeof ReceivableStatusSchema>;

/** `OVERDUE` and `OPEN` (not overdue) both persist as `OPEN` — derived at read time (BR-FIN-15). */
export const ReceivableStatusFilterSchema = z.enum(['all', 'OPEN', 'OVERDUE', 'PAID', 'CANCELLED']);
export type ReceivableStatusFilter = z.infer<typeof ReceivableStatusFilterSchema>;

export const CreateReceivableModeSchema = z.enum(['SINGLE', 'INSTALLMENTS']);
export type CreateReceivableMode = z.infer<typeof CreateReceivableModeSchema>;

// POST /finance/receivables
export const CreateReceivableBodySchema = z
  .object({
    placeId: z.uuid().nullish(),
    accountId: z.uuid(),
    customerPartyId: z.uuid(),
    documentLabel: z.string().trim().min(1).max(80).nullish(),
    mode: CreateReceivableModeSchema,
    totalAmountCents: CentsSchema,
    installmentCount: z.number().int().min(1).max(360).optional().default(1),
    issueDate: z.iso.date(),
    firstDueDate: z.iso.date(),
    operationKind: TitleOperationKindSchema.optional(),
    notes: z.string().trim().max(2000).nullish(),
  })
  .refine((body) => body.mode === 'SINGLE' || body.installmentCount >= 2, {
    message: 'installmentCount must be >= 2 when mode is INSTALLMENTS',
    path: ['installmentCount'],
  });
export type CreateReceivableBody = z.infer<typeof CreateReceivableBodySchema>;

export const CreateReceivableItemResponseSchema = z.object({
  id: z.uuid(),
  installmentNumber: z.number().int().positive(),
  installmentCount: z.number().int().positive(),
  dueDate: z.iso.date(),
  amountCents: CentsSchema,
  status: ReceivableStatusSchema,
});

export const CreateReceivableResponseSchema = z.object({
  documentGroupId: z.uuid(),
  items: z.array(CreateReceivableItemResponseSchema),
});
export type CreateReceivableResponse = z.infer<typeof CreateReceivableResponseSchema>;

// GET /finance/receivables
export const ListReceivablesQuerySchema = PaginationSchema.extend({
  status: ReceivableStatusFilterSchema.optional(),
  placeId: z.string().min(1).optional(),
  customerPartyId: z.uuid().optional(),
  q: z.string().trim().min(1).optional(),
});
export type ListReceivablesQuery = z.infer<typeof ListReceivablesQuerySchema>;

/** `OPERATOR_ENTRY` — launched in Contas a receber (no service order link); `SERVICE_ORDER` — linked to an OS. */
export const ReceivableOriginKindSchema = z.enum(['OPERATOR_ENTRY', 'SERVICE_ORDER']);
export type ReceivableOriginKind = z.infer<typeof ReceivableOriginKindSchema>;

export const ReceivableListItemSchema = z.object({
  id: z.uuid(),
  documentLabel: z.string().nullable(),
  customerDisplayName: z.string(),
  placeId: z.uuid().nullable(),
  placeName: z.string().nullable(),
  accountId: z.uuid(),
  accountName: z.string(),
  issueDate: z.iso.date(),
  dueDate: z.iso.date(),
  amountCents: CentsSchema,
  status: ReceivableStatusSchema,
  isOverdue: z.boolean(),
  installmentNumber: z.number().int().positive(),
  installmentCount: z.number().int().positive(),
  customerPartyId: z.uuid(),
  operationKind: TitleOperationKindSchema,
  notes: z.string().nullable(),
  originKind: ReceivableOriginKindSchema,
});
export type ReceivableListItem = z.infer<typeof ReceivableListItemSchema>;

// PATCH /finance/receivables/:id — OPEN operator entries only (BR-FIN-67)
export const UpdateReceivableBodySchema = z.object({
  placeId: z.uuid().nullish(),
  accountId: z.uuid(),
  customerPartyId: z.uuid(),
  documentLabel: z.string().trim().min(1).max(80).nullish(),
  amountCents: CentsSchema,
  issueDate: z.iso.date(),
  dueDate: z.iso.date(),
  operationKind: TitleOperationKindSchema.optional(),
  notes: z.string().trim().max(2000).nullish(),
});
export type UpdateReceivableBody = z.infer<typeof UpdateReceivableBodySchema>;

export const UpdateReceivableResponseSchema = z.object({
  id: z.uuid(),
  documentLabel: z.string().nullable(),
  dueDate: z.iso.date(),
  amountCents: CentsSchema,
  status: ReceivableStatusSchema,
});
export type UpdateReceivableResponse = z.infer<typeof UpdateReceivableResponseSchema>;

// POST /finance/receivables/cancel (batch — BR-FIN-74)
export const CancelReceivablesBatchBodySchema = z.object({
  receivableIds: z.array(z.uuid()).min(1).max(200),
});
export type CancelReceivablesBatchBody = z.infer<typeof CancelReceivablesBatchBodySchema>;

export const CancelReceivableItemResponseSchema = z.object({
  id: z.uuid(),
  status: z.literal('CANCELLED'),
});
export type CancelReceivableItemResponse = z.infer<typeof CancelReceivableItemResponseSchema>;

export const CancelReceivablesBatchResponseSchema = z.object({
  items: z.array(CancelReceivableItemResponseSchema),
});
export type CancelReceivablesBatchResponse = z.infer<typeof CancelReceivablesBatchResponseSchema>;

export const ListReceivablesResponseSchema = z.object({
  items: z.array(ReceivableListItemSchema),
  pagination: CursorPaginationMetaSchema,
  summary: z.object({
    totalReceivableCents: CentsSchema,
    overdueCents: CentsSchema,
    openCents: CentsSchema,
    receivedThisMonthCents: CentsSchema,
  }),
});
export type ListReceivablesResponse = z.infer<typeof ListReceivablesResponseSchema>;

// POST /finance/receivables/:id/settle
export const ReceivableIdParamsSchema = z.object({ id: z.uuid() });

export const SettleReceivableBodySchema = z.object({
  financialAccountId: z.uuid(),
  settlementMethod: SettlementMethodSchema,
  settledAt: z.iso.date().optional(),
});
export type SettleReceivableBody = z.infer<typeof SettleReceivableBodySchema>;

export const SettleReceivableItemResponseSchema = z.object({
  receivableId: z.uuid(),
  status: z.literal('PAID'),
  settledAt: z.iso.datetime(),
});
export type SettleReceivableItemResponse = z.infer<typeof SettleReceivableItemResponseSchema>;

// POST /finance/receivables/settle (batch — BR-FIN-16)
export const SettleReceivablesBatchBodySchema = z.object({
  receivableIds: z.array(z.uuid()).min(1).max(200),
  financialAccountId: z.uuid(),
  settlementMethod: SettlementMethodSchema,
  settledAt: z.iso.date().optional(),
});
export type SettleReceivablesBatchBody = z.infer<typeof SettleReceivablesBatchBodySchema>;

export const SettleReceivablesBatchResponseSchema = z.object({
  items: z.array(SettleReceivableItemResponseSchema),
});
export type SettleReceivablesBatchResponse = z.infer<typeof SettleReceivablesBatchResponseSchema>;

// GET /places/{placeId}/finance-daily-summary
export const FinanceDailySummaryResponseSchema = z.object({
  receivedTodayCents: CentsSchema,
});
export type FinanceDailySummaryResponse = z.infer<typeof FinanceDailySummaryResponseSchema>;

// --- Fluxo de caixa (BR-FIN-18..22) ---

export const CashMovementSourceTypeSchema = z.enum(['PAYABLE', 'RECEIVABLE', 'MANUAL', 'TRANSFER']);
export type CashMovementSourceType = z.infer<typeof CashMovementSourceTypeSchema>;

export const CashMovementDirectionSchema = z.enum(['IN', 'OUT']);
export type CashMovementDirection = z.infer<typeof CashMovementDirectionSchema>;

// POST /finance/cash-movements/transfers
export const RegisterTransferBodySchema = z
  .object({
    fromFinancialAccountId: z.uuid(),
    toFinancialAccountId: z.uuid(),
    amountCents: CentsSchema,
    movementDate: z.iso.date(),
    description: z.string().trim().min(1).max(160).nullish(),
  })
  .refine((body) => body.fromFinancialAccountId !== body.toFinancialAccountId, {
    message: 'fromFinancialAccountId and toFinancialAccountId must be different',
    path: ['toFinancialAccountId'],
  });
export type RegisterTransferBody = z.infer<typeof RegisterTransferBodySchema>;

export const RegisterTransferResponseSchema = z.object({
  transferGroupId: z.uuid(),
  outMovementId: z.uuid(),
  inMovementId: z.uuid(),
  fromFinancialAccountId: z.uuid(),
  toFinancialAccountId: z.uuid(),
  amountCents: CentsSchema,
  movementDate: z.iso.date(),
});
export type RegisterTransferResponse = z.infer<typeof RegisterTransferResponseSchema>;

// POST /finance/cash-movements/manual
export const RegisterManualCashMovementBodySchema = z.object({
  financialAccountId: z.uuid(),
  movementDate: z.iso.date(),
  direction: CashMovementDirectionSchema,
  amountCents: CentsSchema,
  description: z.string().trim().min(1).max(160),
  partyLabel: z.string().trim().min(1).max(160).nullish(),
  method: z.string().trim().min(1).max(32).nullish(),
  documentLabel: z.string().trim().min(1).max(80).nullish(),
  reconciliationRef: z.string().trim().min(1).max(80).nullish(),
});
export type RegisterManualCashMovementBody = z.infer<typeof RegisterManualCashMovementBodySchema>;

export const RegisterManualCashMovementResponseSchema = z.object({
  id: z.uuid(),
  financialAccountId: z.uuid(),
  movementDate: z.iso.date(),
  direction: CashMovementDirectionSchema,
  amountCents: CentsSchema,
  description: z.string(),
});
export type RegisterManualCashMovementResponse = z.infer<
  typeof RegisterManualCashMovementResponseSchema
>;

// GET /finance/cash-flow/statement
export const CashFlowGranularitySchema = z.enum(['DAY', 'WEEK', 'BIWEEKLY', 'MONTH']);
export type CashFlowGranularity = z.infer<typeof CashFlowGranularitySchema>;

export const CashFlowDirectionFilterSchema = z.enum(['all', 'IN', 'OUT']);
export type CashFlowDirectionFilter = z.infer<typeof CashFlowDirectionFilterSchema>;

export const CashFlowStatusFilterSchema = z.enum(['all', 'REALIZED', 'PROJECTED']);
export type CashFlowStatusFilter = z.infer<typeof CashFlowStatusFilterSchema>;

/** `'ALL'` sentinel selects the consolidated (all accounts) scope. */
export const GetCashFlowStatementQuerySchema = z.object({
  financialAccountId: z
    .union([z.literal('ALL'), z.uuid()])
    .optional()
    .default('ALL'),
  from: z.iso.date(),
  to: z.iso.date(),
  granularity: CashFlowGranularitySchema.optional().default('MONTH'),
  hideInternalTransfers: z
    .union([z.literal('true'), z.literal('false')])
    .optional()
    .default('false')
    .transform((value) => value === 'true'),
  direction: CashFlowDirectionFilterSchema.optional().default('all'),
  status: CashFlowStatusFilterSchema.optional().default('all'),
  q: z.string().trim().min(1).optional(),
});
export type GetCashFlowStatementQuery = z.infer<typeof GetCashFlowStatementQuerySchema>;

export const CashFlowStatementLineSchema = z.object({
  id: z.string(),
  date: z.iso.date(),
  description: z.string(),
  partyLabel: z.string().nullable(),
  method: z.string().nullable(),
  documentLabel: z.string().nullable(),
  valueCents: z.string().regex(/^-?\d+$/),
  direction: CashMovementDirectionSchema,
  kind: z.enum(['REALIZED', 'PROJECTED']),
  sourceType: z.string(),
  financialAccountId: z.uuid().nullable(),
  financialAccountName: z.string().nullable(),
  isTransfer: z.boolean(),
  cashFlowSection: CashFlowSectionSchema,
});
export type CashFlowStatementLine = z.infer<typeof CashFlowStatementLineSchema>;

export const CashFlowSectionTotalsSchema = z.object({
  operatingInCents: CentsSchema,
  operatingOutCents: CentsSchema,
  investingInCents: CentsSchema,
  investingOutCents: CentsSchema,
  financingInCents: CentsSchema,
  financingOutCents: CentsSchema,
});
export type CashFlowSectionTotals = z.infer<typeof CashFlowSectionTotalsSchema>;

export const CashFlowStatementGroupSchema = z.object({
  key: z.string(),
  periodStart: z.iso.date(),
  periodEnd: z.iso.date(),
  openingBalanceCents: z.string().regex(/^-?\d+$/),
  closingBalanceCents: z.string().regex(/^-?\d+$/),
  totalInCents: CentsSchema,
  totalOutCents: CentsSchema,
  ...CashFlowSectionTotalsSchema.shape,
  lines: z.array(CashFlowStatementLineSchema),
});
export type CashFlowStatementGroup = z.infer<typeof CashFlowStatementGroupSchema>;

export const GetCashFlowStatementResponseSchema = z.object({
  financialAccountId: z.uuid().nullable(),
  from: z.iso.date(),
  to: z.iso.date(),
  granularity: CashFlowGranularitySchema,
  openingBalanceCents: z.string().regex(/^-?\d+$/),
  closingBalanceCents: z.string().regex(/^-?\d+$/),
  totalInCents: CentsSchema,
  totalOutCents: CentsSchema,
  ...CashFlowSectionTotalsSchema.shape,
  groups: z.array(CashFlowStatementGroupSchema),
});
export type GetCashFlowStatementResponse = z.infer<typeof GetCashFlowStatementResponseSchema>;

// --- DRE (BR-FIN-23, 25, 43, 44, 51) ---

const SignedCentsSchema = z.string().regex(/^-?\d+$/, 'must be an integer string (cents)');

/** Fixed DRE structure (BR-FIN-44) — 8 postable groups + 5 computed subtotals, in display order. */
export const IncomeStatementLineKeySchema = z.enum([
  'GROSS_REVENUE',
  'DEDUCTIONS',
  'NET_REVENUE',
  'COSTS',
  'GROSS_PROFIT',
  'OPERATING_EXPENSES',
  'EBITDA',
  'DEPRECIATION',
  'FINANCIAL_RESULT',
  'NON_OPERATING',
  'RESULT_BEFORE_TAX',
  'INCOME_TAX',
  'NET_PROFIT',
]);
export type IncomeStatementLineKey = z.infer<typeof IncomeStatementLineKeySchema>;

// GET /finance/income-statement
/** `placeId` omitted = consolidated (BR-FIN-25); `'GERAL'` sentinel filters `place_id IS NULL`. */
export const GetIncomeStatementQuerySchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  placeId: z.string().min(1).optional(),
});
export type GetIncomeStatementQuery = z.infer<typeof GetIncomeStatementQuerySchema>;

export const IncomeStatementLineAccountSchema = z.object({
  accountId: z.uuid(),
  accountCode: z.string(),
  accountName: z.string(),
  amountCents: CentsSchema,
});
export type IncomeStatementLineAccount = z.infer<typeof IncomeStatementLineAccountSchema>;

export const IncomeStatementLineSchema = z.object({
  key: IncomeStatementLineKeySchema,
  isAddition: z.boolean(),
  isSubtotal: z.boolean(),
  amountCents: SignedCentsSchema,
  percentOfGrossRevenue: z.number().nullable(),
  accounts: z.array(IncomeStatementLineAccountSchema),
});
export type IncomeStatementLine = z.infer<typeof IncomeStatementLineSchema>;

export const IncomeStatementUnitSchema = z.object({
  placeId: z.uuid().nullable(),
  placeName: z.string(),
  revenueCents: CentsSchema,
  netProfitCents: SignedCentsSchema,
  netMarginPercent: z.number().nullable(),
});
export type IncomeStatementUnit = z.infer<typeof IncomeStatementUnitSchema>;

export const GetIncomeStatementResponseSchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  /** `null` = consolidated (no filter); `'GERAL'` = places-less bucket; otherwise the filtered place UUID — echoes the request (BR-FIN-25). */
  placeId: z.union([z.literal('GERAL'), z.uuid()]).nullable(),
  lines: z.array(IncomeStatementLineSchema),
  kpis: z.object({
    grossRevenueCents: CentsSchema,
    grossProfitCents: SignedCentsSchema,
    grossProfitPercent: z.number().nullable(),
    ebitdaCents: SignedCentsSchema,
    ebitdaPercent: z.number().nullable(),
    netProfitCents: SignedCentsSchema,
    netProfitPercent: z.number().nullable(),
  }),
  units: z.array(IncomeStatementUnitSchema),
});
export type GetIncomeStatementResponse = z.infer<typeof GetIncomeStatementResponseSchema>;

// --- Payment settings hub (BR-FIN-32..34, 40, 41) ---

export const OsSettlementGateStageSchema = z.enum(['OFF', 'ON_CLOSE', 'ON_DELIVERY_AND_CLOSE']);
export type OsSettlementGateStage = z.infer<typeof OsSettlementGateStageSchema>;

export const CanonicalPaymentMethodSchema = PaymentMethodSchema;
export type CanonicalPaymentMethod = PaymentMethod;

export const RemittanceChannelSchema = z.enum(['API', 'CNAB240', 'CNAB400']);
export type RemittanceChannel = z.infer<typeof RemittanceChannelSchema>;

export const BankAgreementEnvironmentSchema = z.enum(['HOMOLOG', 'PROD']);
export type BankAgreementEnvironment = z.infer<typeof BankAgreementEnvironmentSchema>;

export const BankApiProviderSchema = z.enum(['BRADESCO']);
export type BankApiProvider = z.infer<typeof BankApiProviderSchema>;

// GET|PUT /finance/places/:placeId/payment-settings
export const UpsertPlacePaymentSettingsBodySchema = z.object({
  osSettlementGateStage: OsSettlementGateStageSchema,
  enabledMethods: z.array(CanonicalPaymentMethodSchema).min(1),
  malvoOpenFinanceEnabled: z.boolean().optional(),
});
export type UpsertPlacePaymentSettingsBody = z.infer<typeof UpsertPlacePaymentSettingsBodySchema>;

/** @deprecated Use UpsertPlacePaymentSettingsBodySchema */
export const UpsertTenantPaymentSettingsBodySchema = UpsertPlacePaymentSettingsBodySchema;
export type UpsertTenantPaymentSettingsBody = UpsertPlacePaymentSettingsBody;

export const PlacePaymentSettingsResponseSchema = z.object({
  id: z.uuid().nullable(),
  placeId: z.uuid(),
  osSettlementGateStage: OsSettlementGateStageSchema,
  enabledMethods: z.array(CanonicalPaymentMethodSchema),
  malvoOpenFinanceEnabled: z.boolean(),
  isDefault: z.boolean(),
  updatedAt: z.iso.datetime().nullable(),
});
export type PlacePaymentSettingsResponse = z.infer<typeof PlacePaymentSettingsResponseSchema>;

/** @deprecated Use PlacePaymentSettingsResponseSchema */
export const TenantPaymentSettingsResponseSchema = PlacePaymentSettingsResponseSchema;
export type TenantPaymentSettingsResponse = PlacePaymentSettingsResponse;

export const UpsertPlacePaymentSettingsResponseSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  osSettlementGateStage: OsSettlementGateStageSchema,
  enabledMethods: z.array(CanonicalPaymentMethodSchema),
  malvoOpenFinanceEnabled: z.boolean(),
  updatedAt: z.iso.datetime(),
});
export type UpsertPlacePaymentSettingsResponse = z.infer<
  typeof UpsertPlacePaymentSettingsResponseSchema
>;

/** @deprecated Use UpsertPlacePaymentSettingsResponseSchema */
export const UpsertTenantPaymentSettingsResponseSchema = UpsertPlacePaymentSettingsResponseSchema;
export type UpsertTenantPaymentSettingsResponse = UpsertPlacePaymentSettingsResponse;

// PATCH /finance/financial-accounts/:id/bank-details
export const FinancialAccountIdParamsSchema = z.object({
  id: z.uuid(),
});

export const UpsertFinancialAccountBankDetailsBodySchema = z.object({
  bankCode: z.string().trim().min(1).max(8).nullable(),
  agency: z.string().trim().min(1).max(20).nullable(),
  agencyDigit: z.string().trim().min(1).max(2).nullable().optional(),
  accountNumber: z.string().trim().min(1).max(32).nullable(),
  accountDigit: z.string().trim().min(1).max(2).nullable().optional(),
  bankAccountType: BankAccountTypeSchema.nullable().optional(),
  ispb: z.string().trim().min(8).max(8).nullable().optional(),
  holderName: z.string().trim().min(1).max(160).nullable().optional(),
  holderDocument: z.string().trim().min(11).max(18).nullable().optional(),
});
export type UpsertFinancialAccountBankDetailsBody = z.infer<
  typeof UpsertFinancialAccountBankDetailsBodySchema
>;

export const FinancialAccountBankDetailsResponseSchema = z.object({
  id: z.uuid(),
  kind: FinancialAccountKindSchema,
  bankCode: z.string().nullable(),
  agency: z.string().nullable(),
  agencyDigit: z.string().nullable(),
  accountNumber: z.string().nullable(),
  accountDigit: z.string().nullable(),
  bankAccountType: BankAccountTypeSchema.nullable(),
  ispb: z.string().nullable(),
  holderName: z.string().nullable(),
  holderDocument: z.string().nullable(),
  updatedAt: z.iso.datetime(),
});
export type FinancialAccountBankDetailsResponse = z.infer<
  typeof FinancialAccountBankDetailsResponseSchema
>;

// Bank agreements
export const BankAgreementIdParamsSchema = z.object({
  id: z.uuid(),
});

export const ListBankAgreementsQuerySchema = z.object({
  includeArchived: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => value === 'true'),
});
export type ListBankAgreementsQuery = z.infer<typeof ListBankAgreementsQuerySchema>;

export const CreateBankAgreementBodySchema = z.object({
  financialAccountId: z.uuid(),
  primaryChannel: RemittanceChannelSchema,
  fallbackChannel: RemittanceChannelSchema.nullable().optional(),
  environment: BankAgreementEnvironmentSchema,
  bankCode: z.string().trim().min(1).max(8).nullable().optional(),
  walletCode: z.string().trim().min(1).max(16).nullable().optional(),
  beneficiaryName: z.string().trim().min(1).max(160).nullable().optional(),
  beneficiaryDocument: z.string().trim().min(11).max(18).nullable().optional(),
  covenantCode: z.string().trim().min(1).max(32).nullable().optional(),
  assignorCode: z.string().trim().min(1).max(32).nullable().optional(),
  pixKey: z.string().trim().min(1).max(120).nullable().optional(),
  apiProvider: BankApiProviderSchema.nullable().optional(),
  /** Write-only; never returned on GET. */
  credential: z.string().min(1).max(8_000).nullable().optional(),
});
export type CreateBankAgreementBody = z.infer<typeof CreateBankAgreementBodySchema>;

export const BankAgreementResponseSchema = z.object({
  id: z.uuid(),
  financialAccountId: z.uuid(),
  primaryChannel: RemittanceChannelSchema,
  fallbackChannel: RemittanceChannelSchema.nullable(),
  environment: BankAgreementEnvironmentSchema,
  bankCode: z.string().nullable(),
  walletCode: z.string().nullable(),
  beneficiaryName: z.string().nullable(),
  beneficiaryDocument: z.string().nullable(),
  covenantCode: z.string().nullable(),
  assignorCode: z.string().nullable(),
  pixKey: z.string().nullable(),
  apiProvider: BankApiProviderSchema.nullable(),
  hasCredential: z.boolean(),
  archivedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type BankAgreementResponse = z.infer<typeof BankAgreementResponseSchema>;

export const ListBankAgreementsResponseSchema = z.object({
  items: z.array(BankAgreementResponseSchema),
});
export type ListBankAgreementsResponse = z.infer<typeof ListBankAgreementsResponseSchema>;

export const ArchiveBankAgreementResponseSchema = z.object({
  id: z.uuid(),
  archivedAt: z.iso.datetime(),
});
export type ArchiveBankAgreementResponse = z.infer<typeof ArchiveBankAgreementResponseSchema>;

// Open Finance
export const ConnectOpenFinanceBodySchema = z.object({
  financialAccountId: z.uuid(),
});
export type ConnectOpenFinanceBody = z.infer<typeof ConnectOpenFinanceBodySchema>;

export const OpenFinanceConnectionResponseSchema = z.object({
  id: z.uuid(),
  financialAccountId: z.uuid(),
  provider: z.literal('MALVO'),
  externalItemId: z.string(),
  status: z.string(),
  hasCredential: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type OpenFinanceConnectionResponse = z.infer<typeof OpenFinanceConnectionResponseSchema>;

export const ListOpenFinanceConnectionsResponseSchema = z.object({
  items: z.array(OpenFinanceConnectionResponseSchema),
});
export type ListOpenFinanceConnectionsResponse = z.infer<
  typeof ListOpenFinanceConnectionsResponseSchema
>;

export const MintMalvoConnectTokenBodySchema = z.object({
  financialAccountId: z.uuid(),
});
export type MintMalvoConnectTokenBody = z.infer<typeof MintMalvoConnectTokenBodySchema>;

export const MintMalvoConnectTokenResponseSchema = z.object({
  connectToken: z.string().min(1),
  expiresAt: z.iso.datetime(),
});
export type MintMalvoConnectTokenResponse = z.infer<typeof MintMalvoConnectTokenResponseSchema>;

export const SyncBankStatementOpenFinanceBodySchema = z.object({
  financialAccountId: z.uuid(),
  fromDate: z.iso.date().optional(),
  toDate: z.iso.date().optional(),
});
export type SyncBankStatementOpenFinanceBody = z.infer<
  typeof SyncBankStatementOpenFinanceBodySchema
>;

/**
 * Malvo webhook envelope (docs.malvo.io/webhooks/events).
 * Canonical fields: `event` + `itemId` + `clientUserId` — not nested `item`.
 * `transactionsCount` appears on `transactions/created` and `transactions/updated`.
 */
export const MalvoWebhookBodySchema = z.looseObject({
  event: z.string().min(1),
  eventId: z.string().optional(),
  itemId: z.string().optional(),
  clientUserId: z.string().optional(),
  triggeredBy: z.string().optional(),
  /** Present on `transactions/created` | `transactions/updated` — count of new/changed txs. */
  transactionsCount: z.number().int().nonnegative().optional(),
});
export type MalvoWebhookBody = z.infer<typeof MalvoWebhookBodySchema>;

export const MalvoWebhookResponseSchema = z.object({
  processed: z.boolean(),
});
export type MalvoWebhookResponse = z.infer<typeof MalvoWebhookResponseSchema>;

// --- Cash session (BR-FIN-44..47) ---

export const CashSessionIdParamsSchema = z.object({
  id: z.uuid(),
});

export const GetOpenCashSessionQuerySchema = z.object({
  placeId: z.uuid(),
});
export type GetOpenCashSessionQuery = z.infer<typeof GetOpenCashSessionQuerySchema>;

export const OpenCashSessionBodySchema = z.object({
  placeId: z.uuid(),
  openingFloatCents: CentsSchema,
});
export type OpenCashSessionBody = z.infer<typeof OpenCashSessionBodySchema>;

export const OpenCashSessionResponseSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  financialAccountId: z.uuid(),
  status: z.literal('OPEN'),
  openingFloatCents: CentsSchema,
  openedAt: z.iso.datetime(),
});
export type OpenCashSessionResponse = z.infer<typeof OpenCashSessionResponseSchema>;

export const OpenCashSessionViewSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  financialAccountId: z.uuid(),
  status: z.literal('OPEN'),
  openingFloatCents: CentsSchema,
  openedAt: z.iso.datetime(),
  derivedBalanceCents: CentsSchema,
});

export const GetOpenCashSessionResponseSchema = z.object({
  session: OpenCashSessionViewSchema.nullable(),
});
export type GetOpenCashSessionResponse = z.infer<typeof GetOpenCashSessionResponseSchema>;

export const RegisterCashSangriaBodySchema = z.object({
  amountCents: CentsSchema,
  toCheckingAccountId: z.uuid(),
});
export type RegisterCashSangriaBody = z.infer<typeof RegisterCashSangriaBodySchema>;

export const RegisterCashSangriaResponseSchema = z.object({
  sessionId: z.uuid(),
  transferGroupId: z.uuid(),
  amountCents: CentsSchema,
  toCheckingAccountId: z.uuid(),
});
export type RegisterCashSangriaResponse = z.infer<typeof RegisterCashSangriaResponseSchema>;

export const RegisterCashSuprimentoBodySchema = z.object({
  amountCents: CentsSchema,
  fromCheckingAccountId: z.uuid().nullable().optional(),
});
export type RegisterCashSuprimentoBody = z.infer<typeof RegisterCashSuprimentoBodySchema>;

export const RegisterCashSuprimentoResponseSchema = z.object({
  sessionId: z.uuid(),
  amountCents: CentsSchema,
  transferGroupId: z.uuid().nullable(),
  movementId: z.uuid().nullable(),
});
export type RegisterCashSuprimentoResponse = z.infer<typeof RegisterCashSuprimentoResponseSchema>;

export const CloseCashSessionBodySchema = z.object({
  countedCents: CentsSchema,
  withdrawToCheckingAccountId: z.uuid().nullable().optional(),
});
export type CloseCashSessionBody = z.infer<typeof CloseCashSessionBodySchema>;

export const CloseCashSessionResponseSchema = z.object({
  id: z.uuid(),
  status: z.literal('CLOSED'),
  countedCents: CentsSchema,
  derivedBalanceCents: CentsSchema,
  closedAt: z.iso.datetime(),
  transferGroupId: z.uuid().nullable(),
});
export type CloseCashSessionResponse = z.infer<typeof CloseCashSessionResponseSchema>;

// --- Bank statement OFX reconciliation (BR-FIN-18/19/42) ---

export const ReconciliationStatusSchema = z.enum(['PENDING', 'MATCHED', 'IGNORED']);
export type ReconciliationStatus = z.infer<typeof ReconciliationStatusSchema>;

export const ImportBankStatementOfxFieldsSchema = z.object({
  financialAccountId: z.uuid(),
});
export type ImportBankStatementOfxFields = z.infer<typeof ImportBankStatementOfxFieldsSchema>;

export const ImportBankStatementOfxResponseSchema = z.object({
  id: z.uuid(),
  financialAccountId: z.uuid(),
  source: z.literal('OFX'),
  periodStart: z.iso.date().nullable(),
  periodEnd: z.iso.date().nullable(),
  importedAt: z.iso.datetime(),
  lineCount: z.number().int().nonnegative(),
  autoMatchedCount: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
});
export type ImportBankStatementOfxResponse = z.infer<typeof ImportBankStatementOfxResponseSchema>;

export const SyncBankStatementOpenFinanceResponseSchema =
  ImportBankStatementOfxResponseSchema.extend({
    source: z.literal('OPEN_FINANCE'),
    id: z.union([z.uuid(), z.literal('')]),
  });
export type SyncBankStatementOpenFinanceResponse = z.infer<
  typeof SyncBankStatementOpenFinanceResponseSchema
>;

export const ListBankStatementsQuerySchema = PaginationSchema.extend({
  financialAccountId: z.uuid(),
});
export type ListBankStatementsQuery = z.infer<typeof ListBankStatementsQuerySchema>;

export const BankStatementListItemSchema = z.object({
  id: z.uuid(),
  financialAccountId: z.uuid(),
  source: z.string(),
  provider: z.string().nullable(),
  periodStart: z.iso.date().nullable(),
  periodEnd: z.iso.date().nullable(),
  importedAt: z.iso.datetime(),
  lineCount: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
});
export type BankStatementListItem = z.infer<typeof BankStatementListItemSchema>;

export const ListBankStatementsResponseSchema = z.object({
  items: z.array(BankStatementListItemSchema),
  pagination: CursorPaginationMetaSchema,
});
export type ListBankStatementsResponse = z.infer<typeof ListBankStatementsResponseSchema>;

export const BankStatementIdParamsSchema = z.object({
  id: z.uuid(),
});

export const BankStatementLineIdParamsSchema = z.object({
  id: z.uuid(),
});

export const ListStatementLinesQuerySchema = PaginationSchema.extend({
  status: ReconciliationStatusSchema.optional(),
});
export type ListStatementLinesQuery = z.infer<typeof ListStatementLinesQuerySchema>;

export const BankStatementLineListItemSchema = z.object({
  id: z.uuid(),
  bankStatementId: z.uuid(),
  movementDate: z.iso.date(),
  amountCents: CentsSchema,
  description: z.string().nullable(),
  externalRef: z.string().nullable(),
  reconciliationStatus: ReconciliationStatusSchema,
  matchedCashMovementId: z.uuid().nullable(),
});
export type BankStatementLineListItem = z.infer<typeof BankStatementLineListItemSchema>;

export const ListStatementLinesResponseSchema = z.object({
  items: z.array(BankStatementLineListItemSchema),
  pagination: CursorPaginationMetaSchema,
});
export type ListStatementLinesResponse = z.infer<typeof ListStatementLinesResponseSchema>;

export const StatementMatchSuggestionSchema = z.object({
  cashMovementId: z.uuid(),
  movementDate: z.iso.date(),
  valueCents: CentsSchema,
  description: z.string(),
  documentLabel: z.string().nullable(),
  score: z.number(),
});
export type StatementMatchSuggestion = z.infer<typeof StatementMatchSuggestionSchema>;

export const SuggestStatementMatchesResponseSchema = z.object({
  lineId: z.uuid(),
  suggestions: z.array(StatementMatchSuggestionSchema),
});
export type SuggestStatementMatchesResponse = z.infer<typeof SuggestStatementMatchesResponseSchema>;

export const ConfirmStatementLineMatchBodySchema = z.object({
  cashMovementId: z.uuid(),
});
export type ConfirmStatementLineMatchBody = z.infer<typeof ConfirmStatementLineMatchBodySchema>;

export const ConfirmStatementLineMatchResponseSchema = z.object({
  lineId: z.uuid(),
  bankStatementId: z.uuid(),
  reconciliationStatus: z.literal('MATCHED'),
  matchedCashMovementId: z.uuid(),
});
export type ConfirmStatementLineMatchResponse = z.infer<
  typeof ConfirmStatementLineMatchResponseSchema
>;

export const IgnoreStatementLineResponseSchema = z.object({
  lineId: z.uuid(),
  bankStatementId: z.uuid(),
  reconciliationStatus: z.literal('IGNORED'),
});
export type IgnoreStatementLineResponse = z.infer<typeof IgnoreStatementLineResponseSchema>;

// --- CNAB collection remittance / return (BR-FIN-35..37) ---

export const CollectionChargeKindSchema = z.enum(['BOLETO', 'PIX', 'PAYMENT_LINK']);
export type CollectionChargeKind = z.infer<typeof CollectionChargeKindSchema>;

export const CollectionChargeStatusSchema = z.enum([
  'DRAFT',
  'ISSUED',
  'REGISTERED',
  'IN_REMITTANCE',
  'ACTIVE',
  'PAID',
  'REJECTED',
  'CANCELLED',
]);
export type CollectionChargeStatus = z.infer<typeof CollectionChargeStatusSchema>;

export const CreateCollectionChargeBodySchema = z.object({
  receivableId: z.uuid(),
  bankAgreementId: z.uuid(),
  kind: z.enum(['BOLETO', 'PIX']),
});
export type CreateCollectionChargeBody = z.infer<typeof CreateCollectionChargeBodySchema>;

export const CollectionChargeResponseSchema = z.object({
  id: z.uuid(),
  receivableId: z.uuid(),
  serviceOrderId: z.uuid().nullable(),
  bankAgreementId: z.uuid(),
  kind: CollectionChargeKindSchema,
  status: CollectionChargeStatusSchema,
  ourNumber: z.string().nullable(),
  digitableLine: z.string().nullable(),
  pixTxid: z.string().nullable().optional(),
  channelUsed: RemittanceChannelSchema.nullable(),
  amountCents: CentsSchema,
  dueDate: z.iso.date(),
  location: z.string().nullable().optional(),
  copiaECola: z.string().nullable().optional(),
  createdAt: z.iso.datetime(),
});
export type CollectionChargeResponse = z.infer<typeof CollectionChargeResponseSchema>;

/** POST /finance/webhooks/bradesco/pix — Bacen Pix notify + stub fields. */
export const BradescoPixWebhookBodySchema = z.object({
  txid: z.string().min(1).optional(),
  status: z.string().min(1).optional(),
  valor: z.string().optional(),
  horario: z.string().optional(),
  endToEndId: z.string().optional(),
  pix: z
    .array(
      z.object({
        txid: z.string().min(1).optional(),
        endToEndId: z.string().optional(),
        valor: z.string().optional(),
        horario: z.string().optional(),
      }),
    )
    .optional(),
});
export type BradescoPixWebhookBody = z.infer<typeof BradescoPixWebhookBodySchema>;

export const BradescoPixWebhookResponseSchema = z.object({
  processed: z.boolean(),
  collectionChargeId: z.uuid().nullable(),
  receivableId: z.uuid().nullable(),
});
export type BradescoPixWebhookResponse = z.infer<typeof BradescoPixWebhookResponseSchema>;

export const ListCollectionChargesQuerySchema = z.object({
  status: CollectionChargeStatusSchema.optional(),
  bankAgreementId: z.uuid().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
});
export type ListCollectionChargesQuery = z.infer<typeof ListCollectionChargesQuerySchema>;

export const ListCollectionChargesResponseSchema = z.object({
  items: z.array(CollectionChargeResponseSchema),
});
export type ListCollectionChargesResponse = z.infer<typeof ListCollectionChargesResponseSchema>;

export const CollectionChargeIdParamsSchema = z.object({
  id: z.uuid(),
});
export type CollectionChargeIdParams = z.infer<typeof CollectionChargeIdParamsSchema>;

export const BuildCollectionRemittanceBodySchema = z.object({
  bankAgreementId: z.uuid(),
  chargeIds: z.array(z.uuid()).min(1),
});
export type BuildCollectionRemittanceBody = z.infer<typeof BuildCollectionRemittanceBodySchema>;

export const BuildCollectionRemittanceResponseSchema = z.object({
  id: z.uuid(),
  bankAgreementId: z.uuid(),
  kind: z.literal('COLLECTION'),
  channel: RemittanceChannelSchema,
  status: z.enum(['PENDING', 'BUILT', 'SUBMITTED', 'FAILED']),
  artifactUri: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  chargeIds: z.array(z.uuid()),
  createdAt: z.iso.datetime(),
});
export type BuildCollectionRemittanceResponse = z.infer<
  typeof BuildCollectionRemittanceResponseSchema
>;

export const RemittanceBatchIdParamsSchema = z.object({
  id: z.uuid(),
});

export const ImportCollectionReturnFieldsSchema = z.object({
  bankAgreementId: z.uuid(),
  externalEventId: z.string().min(1).optional(),
});
export type ImportCollectionReturnFields = z.infer<typeof ImportCollectionReturnFieldsSchema>;

export const ImportCollectionReturnOccurrenceSummarySchema = z.object({
  entryConfirmed: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  paid: z.number().int().nonnegative(),
  writtenOff: z.number().int().nonnegative(),
});
export type ImportCollectionReturnOccurrenceSummary = z.infer<
  typeof ImportCollectionReturnOccurrenceSummarySchema
>;

export const ImportCollectionReturnResponseSchema = z.object({
  returnBatchId: z.uuid(),
  status: z.enum(['RECEIVED', 'PROCESSED', 'FAILED']),
  processedCount: z.number().int().nonnegative(),
  paidChargeIds: z.array(z.uuid()),
  settledReceivableIds: z.array(z.uuid()),
  occurrenceSummary: ImportCollectionReturnOccurrenceSummarySchema,
});
export type ImportCollectionReturnResponse = z.infer<typeof ImportCollectionReturnResponseSchema>;

export const BuildPaymentRemittanceItemSchema = z.object({
  payableId: z.uuid(),
  payeeBankCode: z.string().min(1).max(3),
  payeeAgency: z.string().min(1).max(10),
  payeeAccountNumber: z.string().min(1).max(20),
  payeeName: z.string().min(1).max(60).optional(),
  payeeDocument: z.string().min(1).max(18).optional(),
});
export type BuildPaymentRemittanceItem = z.infer<typeof BuildPaymentRemittanceItemSchema>;

export const BuildPaymentRemittanceBodySchema = z.object({
  bankAgreementId: z.uuid(),
  items: z.array(BuildPaymentRemittanceItemSchema).min(1),
});
export type BuildPaymentRemittanceBody = z.infer<typeof BuildPaymentRemittanceBodySchema>;

export const BuildPaymentRemittanceResponseSchema = z.object({
  id: z.uuid(),
  bankAgreementId: z.uuid(),
  kind: z.literal('PAYMENT'),
  channel: RemittanceChannelSchema,
  status: z.enum(['PENDING', 'BUILT', 'SUBMITTED', 'FAILED']),
  artifactUri: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  payableIds: z.array(z.uuid()),
  createdAt: z.iso.datetime(),
});
export type BuildPaymentRemittanceResponse = z.infer<typeof BuildPaymentRemittanceResponseSchema>;

export const ImportPaymentReturnFieldsSchema = z.object({
  bankAgreementId: z.uuid(),
  externalEventId: z.string().min(1).optional(),
});
export type ImportPaymentReturnFields = z.infer<typeof ImportPaymentReturnFieldsSchema>;

export const ImportPaymentReturnOccurrenceSummarySchema = z.object({
  entryConfirmed: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  paid: z.number().int().nonnegative(),
  writtenOff: z.number().int().nonnegative(),
});
export type ImportPaymentReturnOccurrenceSummary = z.infer<
  typeof ImportPaymentReturnOccurrenceSummarySchema
>;

export const ImportPaymentReturnResponseSchema = z.object({
  returnBatchId: z.uuid(),
  status: z.enum(['RECEIVED', 'PROCESSED', 'FAILED']),
  processedCount: z.number().int().nonnegative(),
  paidPayableIds: z.array(z.uuid()),
  occurrenceSummary: ImportPaymentReturnOccurrenceSummarySchema,
});
export type ImportPaymentReturnResponse = z.infer<typeof ImportPaymentReturnResponseSchema>;

export const FinancialEntrySourceTypeSchema = z.enum([
  'STOCK_MOVEMENT',
  'STOCK_TRANSFER',
  'STOCK_TRANSFER_SEND',
  'CASH_TRANSFER',
  'INBOUND_NF_ITEM',
  'INBOUND_NF_CHARGE',
  'INBOUND_NF_CONFERENCE',
  'OUTBOUND_NF_ITEM',
  'OUTBOUND_NF_DOCUMENT',
  'POS_RECEIPT_ITEM',
  'POS_RECEIPT_DOCUMENT',
  'SERVICE_ORDER_ITEM',
  'PAYROLL_ACCRUAL',
  'PAYROLL_PAYMENT',
  'PAYMENT_SETTLEMENT',
  'COLLECTION_CHARGE_SETTLEMENT',
  'RECEIVABLE_ADVANCE',
  'CASH_SESSION_VARIANCE',
  'MONTHLY_CLOSE',
  'MONTHLY_CLOSE_REVERSAL',
]);
export type FinancialEntrySourceType = z.infer<typeof FinancialEntrySourceTypeSchema>;

export const LedgerPeriodScopeSchema = z.enum(['UNIT', 'CONSOLIDATED']);
export type LedgerPeriodScope = z.infer<typeof LedgerPeriodScopeSchema>;

export const GetLedgerPeriodTotalsQuerySchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  scope: LedgerPeriodScopeSchema,
  placeId: z.uuid().optional(),
});
export type GetLedgerPeriodTotalsQuery = z.infer<typeof GetLedgerPeriodTotalsQuerySchema>;

export const LedgerPeriodTotalItemSchema = z.object({
  accountId: z.uuid(),
  accountKey: z.string(),
  dreSection: DreSectionSchema,
  amountCents: z.string().regex(/^-?\d+$/, 'must be an integer string (cents)'),
});
export type LedgerPeriodTotalItem = z.infer<typeof LedgerPeriodTotalItemSchema>;

export const GetLedgerPeriodTotalsResponseSchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  scope: LedgerPeriodScopeSchema,
  placeId: z.uuid().nullable(),
  totals: z.array(LedgerPeriodTotalItemSchema),
});
export type GetLedgerPeriodTotalsResponse = z.infer<typeof GetLedgerPeriodTotalsResponseSchema>;
